import { useTranslation } from 'react-i18next';
import { useOrganizationPurchase } from 'features/organizations/hooks/useOrganizationPurchase';
import {
  Table,
  TableBody,
  tableBodyClasses,
  TableCell,
  tableCellClasses,
  TableRow,
  tableRowClasses,
} from '@mui/material';
import { formatToUSDollars } from 'shared/utils';
import { OrganizationPurchase } from 'features/organizations/organizations.types';
import { LoadingAndErrors } from 'components/LoadingAndErrors';

interface OrganizationPurchaseSummaryProps {
  regularSeats: number;
  proSeats: number;
  showPerPlanType?: boolean;
}

export const OrganizationPurchaseSummary = (
  props: OrganizationPurchaseSummaryProps,
): JSX.Element => {
  const { regularSeats = 0, proSeats = 0, showPerPlanType = false } = props;
  const { t } = useTranslation();

  const {
    previewPurchase: purchasePreview,
    isLoading,
    isError,
  } = useOrganizationPurchase({
    processOrganizationPurchase: {
      regularSeats: regularSeats || 0,
      proSeats: proSeats || 0,
    },
  });

  const summary: Partial<OrganizationPurchase> =
    isLoading || !purchasePreview
      ? { subtotal: undefined, savings: undefined, total: undefined }
      : purchasePreview;

  const displayNumber = (num: number | undefined): string => {
    if (isLoading || isError) {
      return '-';
    }
    return formatToUSDollars(num || 0, 2);
  };

  if (isLoading || isError || summary === undefined) {
    return <LoadingAndErrors isLoading={isLoading} />;
  }

  return (
    <Table
      size="small"
      sx={{
        [`& .${tableCellClasses.root}`]: {
          borderBottom: 'none',
          padding: 0,
          paddingBottom: 0.5,
          fontSize: 'inherit',
        },
        [`& .${tableBodyClasses.root} .${tableRowClasses.root}:first-child .${tableCellClasses.root}`]:
          {
            paddingTop: 2,
          },
      }}
    >
      {showPerPlanType && (
        <TableBody className="plan-type-summary">
          <TableRow>
            <TableCell>
              <b>{t('organizationPurchase.membership')}</b>
            </TableCell>
            <TableCell align="right">{regularSeats}</TableCell>
            <TableCell align="right">
              {displayNumber(summary.planTypeSubtotals?.regular)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <b>{t('organizationPurchase.pro')}</b>
            </TableCell>
            <TableCell align="right">{proSeats}</TableCell>
            <TableCell align="right">
              {displayNumber(summary.planTypeSubtotals?.pro)}
            </TableCell>
          </TableRow>
        </TableBody>
      )}
      <TableBody sx={{ mt: 2 }}>
        <TableRow>
          <TableCell />
          <TableCell align="right">
            {t('organizationPurchase.subtotal')}
          </TableCell>
          <TableCell align="right">{displayNumber(summary.subtotal)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell align="right">
            {t('organizationPurchase.savings')}
          </TableCell>
          <TableCell align="right">{displayNumber(summary.savings)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell align="right">
            <b>{t('organizationPurchase.total')}</b>
          </TableCell>
          <TableCell align="right">
            <b>{displayNumber(summary.total)}</b>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
